// @flow
import React, { Fragment, useState, useEffect } from "react";
import moment from "moment";
import {
  Container,
  Table,
  ReportCell,
  AttributeTd,
  FailLabel,
  WarningLabel,
  PassLabel,
  ProductNameTd,
} from "./Theme/ProductFailureTheme";

function Cell({ row, product_id, data, onClick }) {
  const handleClick = () => {
    onClick({ product_id, date: data.date, attributes: row.row_attributes });
  };
  return (
    <ReportCell className="counts" {...data} onClick={handleClick}>
      {data && (
        <Fragment>
          {data.pass_count > 0 && <PassLabel>{data.pass_count}</PassLabel>}
          {data.fail_count > 0 && <FailLabel>{data.fail_count}</FailLabel>}
          {data.warning_count > 0 && <WarningLabel>{data.warning_count}</WarningLabel>}
        </Fragment>
      )}
    </ReportCell>
  );
}

function calculateMonthColSpans(dates) {
  const months = [];
  dates.forEach((date) => {
    const monthString = moment.utc(date).format("MMM");
    var lastMonth = months[months.length - 1];
    if (!lastMonth || lastMonth.month !== monthString) {
      lastMonth = { month: monthString, span: 1 };
      months.push(lastMonth);
    } else {
      lastMonth.span += 1;
    }
  });
  return months;
}

export default function ProductFailureReport({ data, findSamples, dates }) {
  const { report_attributes: attributes, products } = data;

  const sampleCountPerDay = (date) => {
    var total = 0;
    products.forEach((product) => {
      product.rows.forEach((row) => {
        const rowDate = row.data.find((d) => d.date === date);
        if (rowDate) total += rowDate.sample_count;
      });
    });
    return total;
  };

  const getRowStats = (row) => {
    const stats = {
      sampleCount: 0,
      failedSampleCount: 0,
      passRate: null,
    };
    row.data.forEach((datum) => {
      stats.sampleCount += datum.sample_count;
      stats.failedSampleCount += datum.fail_count;
    });
    stats.passRate = percentagePass(stats.failedSampleCount, stats.sampleCount);
    return stats;
  };

  const percentagePass = (failed, total) => {
    return 100 - Math.round((failed / total) * 100);
  };

  var totalSampleCount = 0;
  var totalFailedSampleCount = 0;
  products.forEach((product) => {
    product.rows.forEach((row) => {
      var stats = getRowStats(row);
      totalSampleCount += stats.sampleCount;
      totalFailedSampleCount += stats.failedSampleCount;
    });
  });

  return (
    <Container>
      <Table>
        <thead>
          <tr>
            <th className="bottom_line product_name" rowSpan={3}>
              {products.length}
              &nbsp;
              {I18n.t("products")}
            </th>
            {attributes.length > 0 && <td colspan={attributes.length} />}
            {calculateMonthColSpans(dates).map((month) => (
              <th
                key={month.month}
                className="date"
                colSpan={month.span}
                style={{ height: 50 }}
              >
                {month.month}
              </th>
            ))}
            <th rowSpan={3} className="bottom_line total_col">
              {I18n.t("total_sample_count")}
            </th>
            <th rowSpan={3} className="bottom_line total_col">
              {I18n.t("total_tailed_samples")}
            </th>
            <th rowSpan={3} className="bottom_line total_col">
              {I18n.t("percentage_pass")}
            </th>
          </tr>
          <tr>
            {attributes.length > 0 && <td colspan={attributes.length} />}
            {dates.map((date) => (
              <th key={date} className="date">
                {moment
                  .utc(date)
                  .local()
                  .format("ddd")}
              </th>
            ))}
          </tr>
          <tr>
            {attributes.map((attribute) => (
              <th key={attribute} className="bottom_line">
                {attribute.name}
              </th>
            ))}
            {dates.map((date) => (
              <th key={date} className="date day bottom_line">
                {moment
                  .utc(date)
                  .local()
                  .format("D")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {products.map((product) =>
            product.rows.map((row, index) => {
              const { sampleCount, failedSampleCount, passRate } = getRowStats(row);
              return (
                <tr key={index}>
                  <ProductNameTd>{index === 0 && product.name}</ProductNameTd>
                  {attributes
                    .map(
                      (attribute) =>
                        row.row_attributes.find((a) => a.name === attribute.name).value
                    )
                    .map((value, i) => (
                      <AttributeTd key={i} className="text" value={value}>
                        {value || "[no value]"}
                      </AttributeTd>
                    ))}
                  {dates.map((date, i) => {
                    const data = row.data.find((d) => d.date === date);
                    return (
                      <Cell
                        key={i}
                        onClick={findSamples}
                        data={data}
                        row={row}
                        product_id={product.product_id}
                      />
                    );
                  })}
                  <td className="c">{sampleCount}</td>
                  <td className="c">{failedSampleCount > 0 && failedSampleCount}</td>
                  <td className="c">{passRate} %</td>
                </tr>
              );
            })
          )}
          <tr>
            <td style={{ textAlign: "right" }} colSpan={1 + attributes.length}>
              {I18n.t("sample_count")}:
            </td>
            {dates.map((date) => (
              <td key={date} className="date">
                {sampleCountPerDay(date)}
              </td>
            ))}
            <td className="c">{totalSampleCount}</td>
            <td className="c">{totalFailedSampleCount}</td>
            <td className="c">{percentagePass(totalFailedSampleCount, totalSampleCount)} %</td>
          </tr>
        </tbody>
      </Table>
    </Container>
  );
}
